/**
 * CR456: Hook for CRUD on flyer_image_capture_adapters
 */
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface ImageCaptureAdapter {
  adapter_key: string;
  display_name: string;
  runtime: string;
  entrypoint: string;
  version: string;
  enabled: boolean;
  default_timeout_ms: number | null;
  notes: string | null;
  param_schema: unknown | null;
  default_params: unknown | null;
  required_context: string[] | null;
  source: string | null;
  artifact_storage_path: string | null;
  artifact_sha256: string | null;
  // CR460: match rules
  match_rules: unknown | null;
  match_rules_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export function useAdapters() {
  const { toast } = useToast();
  const [adapters, setAdapters] = useState<ImageCaptureAdapter[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAdapters = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("flyer_image_capture_adapters")
      .select("*")
      .order("adapter_key");
    if (error) {
      toast({ title: "Error loading adapters", description: error.message, variant: "destructive" });
    } else {
      setAdapters((data ?? []) as ImageCaptureAdapter[]);
    }
    setLoading(false);
  }, [toast]);

  useEffect(() => { fetchAdapters(); }, [fetchAdapters]);

  const createAdapter = async (payload: Partial<ImageCaptureAdapter>) => {
    const { error } = await supabase.from("flyer_image_capture_adapters").insert(payload as any);
    if (error) {
      toast({ title: "Error creating adapter", description: error.message, variant: "destructive" });
      return false;
    }
    toast({ title: "Adapter created" });
    await fetchAdapters();
    return true;
  };

  const updateAdapter = async (key: string, payload: Partial<ImageCaptureAdapter>) => {
    const { error } = await supabase
      .from("flyer_image_capture_adapters")
      .update({ ...payload, updated_at: new Date().toISOString() } as any)
      .eq("adapter_key", key);
    if (error) {
      toast({ title: "Error updating adapter", description: error.message, variant: "destructive" });
      return false;
    }
    toast({ title: "Adapter updated" });
    await fetchAdapters();
    return true;
  };

  const deleteAdapter = async (key: string) => {
    const { error } = await supabase
      .from("flyer_image_capture_adapters")
      .delete()
      .eq("adapter_key", key);
    if (error) {
      toast({ title: "Error deleting adapter", description: error.message, variant: "destructive" });
      return false;
    }
    toast({ title: "Adapter deleted" });
    await fetchAdapters();
    return true;
  };

  return { adapters, loading, fetchAdapters, createAdapter, updateAdapter, deleteAdapter };
}
